/**
 * The status line (artboards `optimal/` decision 7; on by default on desktop, off in Settings ›
 * Appearance and keys). Left: where you are. Middle: the `StatusActions` slot, where a page puts
 * the keys and actions for its selection. Right: `?` keys, machine load (each machine drills to
 * Settings › Machines) and the connection. Machine status moved here from the old rail.
 */
import { Link, useRouterState } from '@tanstack/react-router'
import { shortcutLabel } from '@viko/transcript-ui'
import { useSyncExternalStore } from 'react'
import { PROJECT_VIEWS, projectOfPath, projectViewOfPath, SETTINGS_SECTIONS } from '../../lib/ia.js'
import { useConnection, useMachines, useProjects } from '../../lib/queries.js'
import { useSelection } from '../../lib/selection-actions.js'
import { useAppUi } from '../app-ui.js'
import { CommandEcho, Keycap } from '../inspector/ActionFooter.js'

const KEY = 'viko.statusLine'
const listeners = new Set<() => void>()

/** Per-browser preference; on unless turned off. */
export function statusLineOn(): boolean {
  try {
    return localStorage.getItem(KEY) !== 'off'
  } catch {
    return true
  }
}
export function setStatusLine(on: boolean) {
  try {
    localStorage.setItem(KEY, on ? 'on' : 'off')
  } catch {
    // private window: this page load only
  }
  for (const l of listeners) l()
}
export function useStatusLinePref(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    statusLineOn,
    () => true,
  )
}

const HOME_VIEW_LABEL: Record<string, string> = {
  needs: 'Needs you',
  running: 'Running',
  projects: 'Projects',
}

/** "Home › Needs you", "Viko › History", "Settings › Machines". */
export function useLocationLabel(): string[] {
  const projects = useProjects()
  const loc = useRouterState({ select: (s) => s.location })
  const path = loc.pathname
  if (path.startsWith('/home')) {
    const v = (loc.search as { v?: string }).v
    return ['Home', HOME_VIEW_LABEL[v ?? ''] ?? 'Needs you']
  }
  if (path.startsWith('/settings')) {
    const id = path.split('/')[2]
    const s = SETTINGS_SECTIONS.find((x) => x.id === id)
    return ['Settings', ...(s ? [s.label] : id === 'enroll' ? ['Add a machine'] : [])]
  }
  const pid = projectOfPath(path)
  if (pid) {
    const name = projects.data?.find((p) => p.id === pid)?.name ?? 'Project'
    const view = projectViewOfPath(path)
    if (view === 'run') return [name, 'Conversation']
    if (view === 'work') return [name, 'Work']
    return [name, PROJECT_VIEWS.find((v) => v.id === view)?.label ?? 'Lanes']
  }
  return []
}

/** The selection's top actions with their keys, and the matching `viko` command (click to copy). */
function SelectionActions() {
  const sel = useSelection()
  if (!sel) return null
  const ready = sel.actions.filter((a) => !a.disabledReason)
  const picks = ready.filter((a) => a.option)
  const verbs = ready.filter((a) => !a.option && a.key !== '⏎').slice(0, 4)
  const echo =
    ready.find((a) => a.id === 'suggested' && a.vikoCommand) ??
    picks.find((a) => a.vikoCommand) ??
    ready.find((a) => a.vikoCommand)
  return (
    <span
      className="vs-status__sel"
      role="group"
      aria-label={`Actions for ${sel.title || 'the selection'}`}
    >
      {picks.length ? (
        <span>
          <Keycap>{`1–${picks.length}`}</Keycap> answer
        </span>
      ) : null}
      {verbs.map((a) => (
        <button
          key={a.id}
          type="button"
          className={`vs-status__act${a.danger ? ' is-danger' : ''}`}
          data-action={a.id}
          onClick={a.run}
        >
          <Keycap>{a.key}</Keycap> {a.label}
        </button>
      ))}
      {echo?.vikoCommand ? (
        <CommandEcho command={echo.vikoCommand} className="vs-status__cmd" />
      ) : null}
    </span>
  )
}

export function StatusLine({ actionsRef }: { actionsRef: (el: HTMLElement | null) => void }) {
  const machines = useMachines()
  const conn = useConnection()
  const where = useLocationLabel()
  const ui = useAppUi()
  return (
    <div className="vs-status" role="region" aria-label="Status line">
      <span className="vs-status__where">
        {where.map((w, i) => (
          <span key={w}>
            {i ? <span aria-hidden="true"> › </span> : null}
            {w}
          </span>
        ))}
      </span>
      <span ref={actionsRef} className="vs-status__actions" data-slot="status-actions" />
      <SelectionActions />
      <span className="vs-status__grow" />
      <button
        type="button"
        className="vs-status__keys"
        onClick={() => ui.openShortcuts?.()}
        aria-label="Keyboard shortcuts"
      >
        <kbd>{shortcutLabel('global.help')}</kbd> keys
      </button>
      <ul className="vs-status__machines" aria-label="Machines">
        {(machines.data ?? []).map((m) => {
          const running = m.caps?.running ?? 0
          const max = m.caps?.maxRuns ?? 0
          const hot = !m.online ? 'is-off' : max && running / max >= 0.9 ? 'is-warn' : ''
          return (
            <li key={m.id}>
              <Link
                to="/settings/machines"
                className={`vs-status__machine ${hot}`}
                aria-label={`${m.name}: ${m.online ? `${running} of ${max} runs` : 'offline'}`}
              >
                {m.name} {m.online ? `${running}/${max}` : 'offline'}
              </Link>
            </li>
          )
        })}
      </ul>
      <span className={`vs-status__conn is-${conn}`} role="status">
        <span className="vs-status__dot" aria-hidden="true" />
        {conn === 'live' ? 'live' : conn === 'offline' ? 'offline' : 'connecting'}
      </span>
    </div>
  )
}
